// nudge-api client. Base URL + Firebase config come from Vite env
// (.env.development / .env.production).
//
// DEV SHORTCUT (dev builds only):
//   In the browser console: larkleDevLogin('YOUR_UID', 'YOUR_ID_TOKEN')

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { LOCATIONS } from '../data.js';

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:3010';

const FIREBASE_CONFIG = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
};

const _FB_ENABLED = !!FIREBASE_CONFIG.apiKey;

// ── Auth ──────────────────────────────────────────────────────────────────────
let _app = null;
let _auth = null;
let _uid = null;
let _token = null;     // only used in dev-login mode; Firebase tokens are read fresh
let _devLogin = false;
const _authListeners = [];

export function larkleOnAuth(cb)  { _authListeners.push(cb); }
export function larkleIsAuthed()  { return !!_uid; }
export function larkleGetUid()    { return _uid; }
export function larkleGetUser()   { return _auth?.currentUser ?? null; }
export function larkleGetApp()    { return _app; }
function _notifyAuth(s)          { _authListeners.forEach(f => f(s)); }

// Dev helper: paste a Firebase ID token directly from the browser console.
// Stripped from production builds (import.meta.env.DEV guard at the bottom).
function larkleDevLogin(uid, token) {
  _devLogin = true;
  _uid = uid; _token = token;
  _notifyAuth({ uid, token });
}

export function larkleInitAuth() {
  if (!_FB_ENABLED) return;
  _app = initializeApp(FIREBASE_CONFIG);
  _auth = getAuth(_app);
  // Surface errors from the signInWithRedirect fallback flow
  getRedirectResult(_auth).catch(err => console.warn('Redirect sign-in failed:', err));
  // Fires on sign-in, sign-out, AND token refresh — no manual refresh timer,
  // and nothing leaks across sign-outs.
  let lastUid = null;
  onIdTokenChanged(_auth, user => {
    if (user) {
      _uid = user.uid;
      if (user.uid !== lastUid) {
        lastUid = user.uid;
        _notifyAuth({ uid: user.uid });
      }
    } else {
      lastUid = null;
      if (!_devLogin) { _uid = null; _token = null; _notifyAuth(null); }
    }
  });
}

export async function larkleSignIn() {
  if (!_FB_ENABLED) {
    alert('Firebase is not configured (VITE_FIREBASE_* env vars missing).');
    return;
  }
  const provider = new GoogleAuthProvider();
  try {
    await signInWithPopup(_auth, provider);
  } catch (err) {
    // Popups are unreliable/blocked on iOS Safari and some in-app browsers —
    // fall back to a full-page redirect for environment errors only.
    const fallbackCodes = [
      'auth/popup-blocked',
      'auth/operation-not-supported-in-this-environment',
      'auth/cancelled-popup-request',
    ];
    if (fallbackCodes.includes(err?.code)) {
      await signInWithRedirect(_auth, provider);
    } else if (err?.code !== 'auth/popup-closed-by-user') {
      throw err;
    }
  }
}

export async function larkleSignOut() {
  _devLogin = false;
  _uid = null; _token = null;
  if (_FB_ENABLED && _auth) await firebaseSignOut(_auth);
  _notifyAuth(null);
}

// ── HTTP ──────────────────────────────────────────────────────────────────────
async function _bearer() {
  if (_auth?.currentUser) return _auth.currentUser.getIdToken(); // cached + auto-refreshed
  return _token;
}

async function _fetch(path, opts = {}, { auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  // Dev-only: bypass the ngrok free-tier browser interstitial when the API
  // is tunneled (it would otherwise return HTML instead of JSON). The header
  // alone doesn't help here: Authorization below forces a CORS preflight, and
  // the browser-generated OPTIONS request can't carry custom header values,
  // so ngrok would still intercept it. Query params ride along on the
  // preflight's URL, so ngrok sees the bypass there too.
  let url = API_BASE + path;
  if (import.meta.env.DEV) {
    headers['ngrok-skip-browser-warning'] = 'true';
    url += (path.includes('?') ? '&' : '?') + 'ngrok-skip-browser-warning=true';
  }
  if (auth) {
    const token = await _bearer();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  let res;
  try {
    res = await fetch(url, { ...opts, headers });
  } catch (err) {
    // fetch TypeError = network failure — flag so surfaces can say "offline"
    const e = new Error(`Network error: ${path}`);
    e.offline = true;
    throw e;
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw Object.assign(new Error(`API ${res.status}: ${path}`), { status: res.status, body });
  }
  return res.status === 204 ? null : res.json();
}

// ── Feed ──────────────────────────────────────────────────────────────────────
export async function apiFetchNearby(coords, radius = 10, paginationToken = null, curatedMode = true) {
  const q = new URLSearchParams({ curatedMode, openNow: false });
  if (paginationToken) q.set('paginationToken', paginationToken);
  return _fetch(`/feed/nearMe/${_uid}/coords/${encodeURIComponent(coords)}/radius/${radius}?${q}`);
}

export async function apiMarkViewed(views) {
  return _fetch(`/feed/markViewed/${_uid}/places`, {
    method: 'POST', body: JSON.stringify(views),
  });
}

export async function apiFetchViewed(status, { limit = 25, offset = 0, queryString, coords, radius } = {}) {
  const q = new URLSearchParams({ limit, offset });
  if (status) q.set('locationViewStatus', status);
  if (queryString) q.set('queryString', queryString);
  if (coords)  q.set('coords', coords);
  if (radius != null) q.set('radius', radius);
  return _fetch(`/feed/viewed/${_uid}?${q}`);
}

// View history with places + view metadata (saveStatus, viewTimeSec, rating).
// Rebuilds the done/passport screen on any device.
export async function apiFetchMyViews(status, { limit = 50, offset = 0 } = {}) {
  const q = new URLSearchParams({ limit, offset });
  if (status) q.set('locationViewStatus', status);
  return _fetch(`/feed/views/${_uid}?${q}`);
}

// Recent places the user's friends marked done, newest first, with friend
// identity + rating. Backs the friends-activity card stack.
export async function apiFetchFriendsActivity(limit = 50) {
  return _fetch(`/feed/friendsActivity/${_uid}?limit=${limit}`);
}

export async function apiFetchLocation(id) { return _fetch(`/feed/location/${id}`); }
// Signed out, the same review-derived copy comes from the public mirror of
// this endpoint (sanitized to review text only), so the guest detail view
// loads its description exactly like the signed-in one.
export async function apiFetchPlaceReviews(id) {
  if (!_uid) return _fetch(`/public/location/${id}/placeInfo`, {}, { auth: false });
  return _fetch(`/feed/location/${id}/placeInfo`);
}

// Unauthenticated sanitized place — backs public share links (/a/:id signed out)
export async function apiFetchPublicLocation(id) {
  return _fetch(`/public/location/${id}`, {}, { auth: false });
}

// Unauthenticated nearby feed — backs the signed-out guest deck. Same
// paginated shape as apiFetchNearby but with sanitized PublicPlace entries
// (adapt with publicPlaceToActivity).
export async function apiFetchPublicNearby(coords, radius, paginationToken = null) {
  const q = paginationToken ? `?${new URLSearchParams({ paginationToken })}` : '';
  return _fetch(`/public/nearby/coords/${encodeURIComponent(coords)}/radius/${radius}${q}`, {}, { auth: false });
}

// Human copy for sign-in failures — a raw Firebase code in a toast reads like
// a stack trace at the exact moment someone is trying to join.
export function signInErrorMessage(err) {
  switch (err?.code) {
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in window. Allow popups and try again.';
    case 'auth/network-request-failed':
      return "You're offline. Check your connection and try again.";
    default:
      return "Couldn't sign you in. Give it another try.";
  }
}

// ── User ──────────────────────────────────────────────────────────────────────
export async function apiGetPreferences()     { return _fetch(`/user/getPreferences/${_uid}`); }
export async function apiUpdatePreferences(p) {
  return _fetch(`/user/updatePreferences/${_uid}`, { method: 'POST', body: JSON.stringify(p) });
}
export async function apiAddReview(id, text) {
  return _fetch(`/user/addReview/${_uid}/place/${id}`, { method: 'POST', body: JSON.stringify(text) });
}
export async function apiRegisterDeviceToken(token, platform = 'Web') {
  return _fetch(`/user/${_uid}/deviceToken`, { method: 'POST', body: JSON.stringify({ token, platform }) });
}
export async function apiRemoveDeviceToken(token) {
  return _fetch(`/user/${_uid}/deviceToken/${encodeURIComponent(token)}`, { method: 'DELETE' });
}
export async function apiDeleteAccount() {
  return _fetch(`/user/${_uid}`, { method: 'DELETE' });
}
// Own server-side identity ({id, name?, handle?, photoUrl?}) — the source of
// truth for the claimed handle on a fresh device.
export async function apiGetProfile() {
  return _fetch(`/user/${_uid}/profile`);
}
// Push the Google profile up so friends/chats/notifications show a real name
// instead of a fallback. Called once per sign-in.
export async function apiSyncProfile(profile) {
  return _fetch(`/user/${_uid}/profile`, { method: 'POST', body: JSON.stringify(profile) });
}

// ── Place → Activity ──────────────────────────────────────────────────────────
function _vibeFromCats(cats) {
  const s = (cats || []).map(c => c.name.toLowerCase()).join(' ');
  if (/park|trail|outdoor|beach|run|sport|fitness|gym|garden/.test(s))
    return { physical: 80, social: 28, outdoor: 90, newPeople: 20, creative: 20, boozy: 5,  eating: 10, expensive: 10, nsfw: 0,  fun: 60 };
  if (/bar|club|nightlife|lounge|brewery|cocktail|pub|tavern/.test(s))
    return { physical: 30, social: 85, outdoor: 20, newPeople: 65, creative: 10, boozy: 90, eating: 25, expensive: 55, nsfw: 40, fun: 80 };
  if (/restaurant|diner|dinner|brunch|bistro/.test(s))
    return { physical: 20, social: 70, outdoor: 30, newPeople: 30, creative: 15, boozy: 40, eating: 95, expensive: 60, nsfw: 5,  fun: 60 };
  if (/cafe|coffee|bakery|tea/.test(s))
    return { physical: 10, social: 35, outdoor: 20, newPeople: 15, creative: 40, boozy: 5,  eating: 55, expensive: 20, nsfw: 0,  fun: 40 };
  if (/museum|gallery|theater|cinema/.test(s))
    return { physical: 20, social: 40, outdoor: 10, newPeople: 10, creative: 70, boozy: 5,  eating: 10, expensive: 35, nsfw: 10, fun: 55 };
  if (/comedy|game|trivia|live|show/.test(s))
    return { physical: 15, social: 75, outdoor: 15, newPeople: 40, creative: 60, boozy: 55, eating: 20, expensive: 45, nsfw: 35, fun: 90 };
  if (/shop|market|store|boutique/.test(s))
    return { physical: 30, social: 30, outdoor: 50, newPeople: 10, creative: 35, boozy: 5,  eating: 20, expensive: 40, nsfw: 0,  fun: 45 };
  return   { physical: 50, social: 50, outdoor: 50, newPeople: 40, creative: 40, boozy: 30, eating: 40, expensive: 40, nsfw: 10, fun: 60 };
}

function _artFromCats(cats) {
  const s = (cats || []).map(c => c.name.toLowerCase()).join(' ');
  if (/park|outdoor|nature|trail|garden/.test(s)) return 'forest-path';
  if (/bar|brewery|pub|tavern|cocktail/.test(s))  return 'pub-glow';
  if (/restaurant|diner|dinner|food/.test(s))     return 'fish-lemon';
  if (/cafe|coffee/.test(s))                       return 'espresso-window';
  if (/museum|gallery/.test(s))                    return 'arcade-glow';
  if (/theater|comedy|show/.test(s))               return 'spotlight-mic';
  return 'copper-kettle';
}

export function placeToActivity(place) {
  const cats = place.categories || [];
  const names = cats.map(c => c.name);
  const open = ['VeryLikelyOpen', 'LikelyOpen'].includes(place.closedBucket);

  return {
    id:           place.id,
    name:         place.name,
    kind:         names[0] || 'Activity',
    neighborhood: '',
    address:      place.address || '',
    lat:          place.lat,
    lon:          place.lon,
    price:        1,
    vibe:         _vibeFromCats(cats),
    tags:         names.slice(0, 5),
    blurb:        place.description || place.name,
    why:          names.slice(0, 2).join(' · ') || 'Near you',
    open,
    hue:          ((place.categoryIds?.[0] || 0) * 47 + 12) % 360,
    art:          _artFromCats(cats),
    // The viewer's own Saved/Liked/Disliked status, attached server-side from
    // the same response — not something to derive by comparing ids across
    // separate fetches (each place gets a freshly re-encrypted id every time
    // it's returned, so ids never match across two different responses).
    viewStatus:   place.viewStatus || null,
    _place:       place,   // original Place kept for markViewed payloads
  };
}

// The public endpoint returns a sanitized shape (categories are plain strings,
// photos are imageUrls) — adapt it to the same Activity the UI renders.
export function publicPlaceToActivity(pub) {
  const cats = (pub.categories || []).map((name, i) => ({ id: i, name }));
  return {
    ...placeToActivity({ ...pub, categories: cats, categoryIds: [] }),
    open: true,
    _place: { ...pub, categories: cats, images: (pub.imageUrls || []).map(url => ({ url })) },
  };
}

// Build a LocationView payload for POST /feed/markViewed. `rating` (optional,
// for Liked/done views): { stars, vibe, value, again, note }.
const _FALLBACK = LOCATIONS[0];
export function placeToLocationView(activity, saveStatus, rating) {
  const p = activity._place;
  return {
    id:            activity.id,
    name:          activity.name,
    description:   activity.blurb,
    categories:    p?.categories || activity.tags.map((n, i) => ({ id: i, name: n })),
    categoryIds:   p?.categoryIds || [],
    coords:        p ? `${p.lat},${p.lon}` : `${_FALLBACK.lat},${_FALLBACK.lon}`,
    geohash:       p?.geoHash || '',
    saveStatus,
    viewTimeSec:   Math.floor(Date.now() / 1000),
    imageQueryB64: p?.imageQueryB64,
    ...(rating ? { rating } : {}),
  };
}

// Server LocationView → the client's done-entry shape ({id, when, whenTs,
// stars, axes, note} — what RateSheet produces and DoneScreen renders).
export function viewToDoneEntry(view) {
  const ts = (view.viewTimeSec || 0) * 1000;
  return {
    id: view.id,
    when: ts ? new Date(ts).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '',
    whenTs: ts,
    stars: view.rating?.stars ?? 0,
    axes: {
      vibe:  view.rating?.vibe  ?? 0,
      value: view.rating?.value ?? 0,
      again: view.rating?.again ?? 0,
    },
    note: view.rating?.note ?? '',
  };
}

// ── Friends ───────────────────────────────────────────────────────────────────
export async function apiFetchFriends() { return _fetch(`/friends/${_uid}`); }
export async function apiFetchIncomingRequests() { return _fetch(`/friends/${_uid}/requests/incoming`); }
export async function apiFetchOutgoingRequests() { return _fetch(`/friends/${_uid}/requests/outgoing`); }
export async function apiSendFriendRequest(target) {
  return _fetch(`/friends/${_uid}/request`, { method: 'POST', body: JSON.stringify(target) });
}
export async function apiRespondToRequest(requestId, accept) {
  return _fetch(`/friends/${_uid}/request/${requestId}/respond`, {
    method: 'POST', body: JSON.stringify({ accept }),
  });
}
export async function apiCancelFriendRequest(requestId) {
  return _fetch(`/friends/${_uid}/request/${requestId}`, { method: 'DELETE' });
}
export async function apiRemoveFriend(friendId) {
  return _fetch(`/friends/${_uid}/friend/${friendId}`, { method: 'DELETE' });
}
export async function apiSearchUser(query) {
  const isEmail = query.includes('@') && !query.startsWith('@');
  const q = new URLSearchParams();
  if (isEmail) q.set('email', query);
  else q.set('handle', query.replace(/^@/, ''));
  return _fetch(`/friends/${_uid}/search?${q}`);
}
export async function apiClaimHandle(handle) {
  return _fetch(`/friends/${_uid}/handle`, { method: 'POST', body: JSON.stringify({ handle }) });
}
export async function apiCheckHandle(handle) {
  return _fetch(`/friends/${_uid}/handle/availability?handle=${encodeURIComponent(handle)}`);
}
export async function apiGenerateInviteCode() {
  return _fetch(`/friends/${_uid}/invite-code`, { method: 'POST' });
}
export async function apiRedeemInvite(code) {
  return _fetch(`/friends/${_uid}/redeem-invite`, { method: 'POST', body: JSON.stringify({ code }) });
}

// ── Shares (send a place to friends) ─────────────────────────────────────────
export async function apiSendShare(toUserIds, placeId, note) {
  return _fetch(`/share/${_uid}`, {
    method: 'POST', body: JSON.stringify({ toUserIds, placeId, ...(note ? { note } : {}) }),
  });
}
export async function apiFetchShareInbox({ limit, includeRead } = {}) {
  const q = new URLSearchParams();
  if (limit != null) q.set('limit', limit);
  if (includeRead != null) q.set('includeRead', includeRead);
  const qs = q.toString();
  return _fetch(`/share/${_uid}/inbox${qs ? `?${qs}` : ''}`);
}
export async function apiMarkSharesRead(shareIds) {
  return _fetch(`/share/${_uid}/read`, { method: 'POST', body: JSON.stringify({ shareIds }) });
}

// ── Chat ──────────────────────────────────────────────────────────────────────
// Conversations are identified by their member set: starting one with the
// same people always returns the same thread (the server dedupes).
export async function apiFetchConversations(limit) {
  const q = limit != null ? `?limit=${limit}` : '';
  return _fetch(`/chat/${_uid}/conversations${q}`);
}
export async function apiStartConversation(memberIds, title) {
  return _fetch(`/chat/${_uid}/conversations`, {
    method: 'POST', body: JSON.stringify({ memberIds, ...(title ? { title } : {}) }),
  });
}
export async function apiGetConversation(conversationId) {
  return _fetch(`/chat/${_uid}/conversations/${conversationId}`);
}
// Newest first; pass `before` (epoch ms) to page back
export async function apiFetchMessages(conversationId, { limit = 50, before } = {}) {
  const q = new URLSearchParams({ limit });
  if (before != null) q.set('before', before);
  return _fetch(`/chat/${_uid}/conversations/${conversationId}/messages?${q}`);
}
export async function apiSendMessage(conversationId, { text, placeId }) {
  return _fetch(`/chat/${_uid}/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ ...(text ? { text } : {}), ...(placeId ? { placeId } : {}) }),
  });
}
export async function apiMarkConversationRead(conversationId) {
  return _fetch(`/chat/${_uid}/conversations/${conversationId}/read`, { method: 'POST' });
}

// ── Plans ─────────────────────────────────────────────────────────────────────
export async function apiFetchPlans(status) {
  const q = status ? `?status=${status}` : '';
  return _fetch(`/plan/${_uid}/list${q}`);
}
export async function apiGetPlan(planId) {
  return _fetch(`/plan/${_uid}/${planId}`);
}
export async function apiCreatePlan(input) {
  return _fetch(`/plan/${_uid}`, { method: 'POST', body: JSON.stringify(input) });
}
export async function apiUpdatePlan(planId, input) {
  return _fetch(`/plan/${_uid}/${planId}`, { method: 'PATCH', body: JSON.stringify(input) });
}
export async function apiInviteToPlan(planId, inviteeIds) {
  return _fetch(`/plan/${_uid}/${planId}/invite`, {
    method: 'POST', body: JSON.stringify({ inviteeIds }),
  });
}
export async function apiRsvpToPlan(planId, rsvp) {
  return _fetch(`/plan/${_uid}/${planId}/rsvp`, {
    method: 'POST', body: JSON.stringify({ rsvp }),
  });
}
export async function apiFinalizePlan(planId, placeId) {
  return _fetch(`/plan/${_uid}/${planId}/finalize`, {
    method: 'POST', body: JSON.stringify({ placeId }),
  });
}
export async function apiCancelPlan(planId) {
  return _fetch(`/plan/${_uid}/${planId}/cancel`, { method: 'POST' });
}
export async function apiLeavePlan(planId) {
  return _fetch(`/plan/${_uid}/${planId}/leave`, { method: 'POST' });
}
export async function apiRemoveFromPlan(planId, inviteeId) {
  return _fetch(`/plan/${_uid}/${planId}/invitee/${inviteeId}`, { method: 'DELETE' });
}

if (import.meta.env.DEV) {
  window.larkleDevLogin = larkleDevLogin;
}
